import {
  List,
  ListRow,
  PageHeader,
  phoneQuery,
  Section,
  Stack,
  TabNav,
  useMediaQuery,
  type TabNavItem,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { PageActionsProvider, PageActionsSlot } from '../../../app/PageActions';
import { RouterLink } from '../../../app/RouterLink';
import { useSession } from '../session';

const ROOT = '/einstellungen';

/**
 * Frame of the settings: title, tabs and the sub-page's primary action. Like
 * every other page it uses the full content width (#176); form tabs put
 * their cards side by side on wide screens (`Columns`).
 * Phones (#60) get an iOS-like list instead of the tabs; each sub-page then
 * has its own title and leads back to the list (or, one level deeper, to its
 * sub-page).
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { can } = useSession();
  const phone = useMediaQuery(phoneQuery);
  const tabs: TabNavItem[] = (
    [
      {
        key: 'business',
        label: t('settings.business'),
        href: '/einstellungen/imkerei',
      },
      ...(can('members.read')
        ? ([
            {
              key: 'members',
              label: t('settings.members'),
              href: '/einstellungen/mitglieder',
            },
            {
              key: 'roles',
              label: t('settings.roles'),
              href: '/einstellungen/rollen',
            },
          ] satisfies TabNavItem[])
        : []),
      ...(can('treatments.read') || can('feedings.read')
        ? ([
            {
              key: 'catalogs',
              label: t('settings.catalogs'),
              href: '/einstellungen/kataloge',
            },
          ] satisfies TabNavItem[])
        : []),
      ...(can('sales.read')
        ? ([
            {
              key: 'sales',
              label: t('settings.sales'),
              href: '/einstellungen/verkauf',
            },
          ] satisfies TabNavItem[])
        : []),
      { key: 'profile', label: t('settings.profile'), href: '/einstellungen/profil' },
      {
        key: 'notifications',
        label: t('settings.notifications'),
        href: '/einstellungen/benachrichtigungen',
      },
      {
        key: 'sessions',
        label: t('settings.sessions'),
        href: '/einstellungen/sitzungen',
      },
      {
        key: 'connections',
        label: t('settings.connections'),
        href: '/einstellungen/verbundene-apps',
      },
      ...(can('data.export')
        ? ([
            {
              key: 'export',
              label: t('settings.dataExport'),
              href: '/einstellungen/datenexport',
            },
          ] satisfies TabNavItem[])
        : []),
      { key: 'about', label: t('settings.about'), href: '/einstellungen/ueber' },
    ] satisfies TabNavItem[]
  ).map((tab) => ({ ...tab, active: pathname.startsWith(tab.href) }));
  const current = tabs.find((tab) => tab.active);

  if (phone) {
    if (!current) {
      return (
        <Stack gap={4}>
          <PageHeader title={t('settings.title')} />
          <Section>
            <List>
              {tabs.map((tab) => (
                <ListRow key={tab.key} title={tab.label} href={tab.href} />
              ))}
            </List>
          </Section>
        </Stack>
      );
    }
    const nested = pathname.replace(/\/$/, '') !== current.href;
    return (
      <PageActionsProvider>
        <Stack gap={4}>
          <PageHeader
            title={current.label}
            back={
              nested ? (
                <BackLink to={current.href} label={current.label} />
              ) : (
                <BackLink to={ROOT} label={t('settings.title')} />
              )
            }
            actions={<PageActionsSlot />}
          />
          <Outlet />
        </Stack>
      </PageActionsProvider>
    );
  }

  return (
    <PageActionsProvider>
      <Stack gap={4}>
        <PageHeader title={t('settings.title')} actions={<PageActionsSlot />} />
        <TabNav label={t('settings.tabs')} items={tabs} linkComponent={RouterLink} />
        <Outlet />
      </Stack>
    </PageActionsProvider>
  );
}

/** `/einstellungen`: the list on phones (see the layout), the first tab elsewhere. */
export function SettingsIndex() {
  const phone = useMediaQuery(phoneQuery);
  return phone ? null : <Navigate to="imkerei" replace />;
}
